/**
 * AURA Profile Definitions
 * Pre-configured accessibility profiles
 */

import {
  AuraSettings,
  ProfileDefinition,
  ProfileName,
  createDefaultSettings,
} from '../../core/types';

export const PROFILE_DEFINITIONS: Record<ProfileName, ProfileDefinition> = {
  seizureSafe: {
    name: 'Seizure Safe Profile',
    description: 'Clear flashes & reduces color',
    icon: 'seizureSafe',
    settings: {
      orientation: {
        stopAnimations: true,
        muteSounds: true,
      },
      color: {
        lowSaturation: true,
      },
    },
  },

  visionImpaired: {
    name: 'Vision Impaired Profile',
    description: "Enhances website's visuals",
    icon: 'visionImpaired',
    settings: {
      content: {
        fontSizing: 22,
        lineHeight: 1.8,
        highlightLinks: true,
      },
      color: {
        highContrast: true,
      },
      orientation: {
        highlightFocus: true,
      },
    },
  },

  adhdFriendly: {
    name: 'ADHD Friendly Profile',
    description: 'More focus & fewer distractions',
    icon: 'adhd',
    settings: {
      orientation: {
        stopAnimations: true,
        muteSounds: true,
        readingMask: true,
      },
      color: {
        lowSaturation: true,
      },
    },
  },

  cognitiveDisability: {
    name: 'Cognitive Disability Profile',
    description: 'Assists with reading & focusing',
    icon: 'cognitive',
    settings: {
      content: {
        lineHeight: 2.0,
        letterSpacing: 1,
        readableFont: true,
        highlightTitles: true,
        highlightLinks: true,
      },
    },
  },

  keyboardNavigation: {
    name: 'Keyboard Navigation (Motor)',
    description: 'Use website with the keyboard',
    icon: 'keyboard',
    playSound: true,
    linkedWith: 'screenReader',
    settings: {
      orientation: {
        highlightFocus: true,
        highlightHover: true,
        bigCursors: true,
      },
    },
  },

  screenReader: {
    name: 'Blind Users (Screen Reader)',
    description: 'Optimize website for screen-readers',
    icon: 'screenReader',
    playSound: true,
    linkedWith: 'keyboardNavigation',
    settings: {
      orientation: {
        highlightFocus: true,
        highlightHover: true,
        bigCursors: true,
      },
    },
  },
};

/**
 * Get profile definition by name
 */
export function getProfileDefinition(name: ProfileName): ProfileDefinition | undefined {
  return PROFILE_DEFINITIONS[name];
}

/**
 * Get merged settings (defaults + profile overrides) for a profile.
 * Returns null if the profile name is not recognized.
 */
export function getProfileSettings(name: ProfileName): AuraSettings | null {
  const definition = PROFILE_DEFINITIONS[name];
  if (!definition) return null;

  const defaults = createDefaultSettings();
  return {
    content: { ...defaults.content, ...definition.settings.content },
    color: { ...defaults.color, ...definition.settings.color },
    orientation: { ...defaults.orientation, ...definition.settings.orientation },
  };
}

/**
 * Get all profile names
 */
export function getAllProfileNames(): ProfileName[] {
  return Object.keys(PROFILE_DEFINITIONS) as ProfileName[];
}
